<svelte:options runes={false} />
<script>
  // Overview tab (U12): headline metrics + equity/drawdown/returns charts + the full tear-sheet + lifecycle
  // controls. Composes shared LEAF components fresh in the tab (PerformancePanel + chart primitives +
  // LifecycleControls) — not inherited from the Backtester container (KTD10). The host owns the lifecycle API
  // calls; this tab just dispatches the operator's intent up through the passed handlers.
  import { isResultMeaningful } from '../../../lib/strategyResult.js';
  import api from '../../../lib/api.js';
  import { selectedOrgId } from '../../../lib/orgContext.js';
  import { strategyErrors, recentBarCounts, fmtErrorTime as fmtTime } from '../../../lib/strategyErrors.js';

  import LifecycleControls from '../LifecycleControls.svelte';
  import PerformancePanel from '../../shared/PerformancePanel.svelte';
  import PositionsTable from '../../shared/PositionsTable.svelte';
  import EquityCurveChart from '../../shared/charts/EquityCurveChart.svelte';
  import DrawdownChart from '../../shared/charts/DrawdownChart.svelte';
  import ReturnsChart from '../../shared/charts/ReturnsChart.svelte';
  import RollingVolatilityChart from '../../shared/charts/RollingVolatilityChart.svelte';
  import RollingSharpeChart from '../../shared/charts/RollingSharpeChart.svelte';
  import UnderwaterChart from '../../shared/charts/UnderwaterChart.svelte';
  import CumulativeVsBenchmarkChart from '../../shared/charts/CumulativeVsBenchmarkChart.svelte';
  import AnnualReturnsChart from '../../shared/charts/AnnualReturnsChart.svelte';
  import MonthlyReturnsHeatmap from '../../shared/charts/MonthlyReturnsHeatmap.svelte';
  import ReturnDistributionChart from '../../shared/charts/ReturnDistributionChart.svelte';
  import ReturnQuantilesChart from '../../shared/charts/ReturnQuantilesChart.svelte';
  import DrawdownPeriodsTable from '../../shared/charts/DrawdownPeriodsTable.svelte';

  export let strategy = null;
  export let status = null;
  export let result = null;
  export let loading = false;
  export let actionBusy = false;

  export let onStart = () => {};
  export let onPause = () => {};
  export let onResume = () => {};
  export let onStop = () => {};
  export let onRemove = () => {};
  export let onGoLive = null;
  export let canGoLive = false;

  $: meaningful = isResultMeaningful(result);
  $: tearsheet = result?.tearsheet ?? null;

  // Positions are self-fetched here (the tab lazy-mounts), scoped to the SAME org as the performance read so the
  // backend org-isolates the ledger scan. null = not yet loaded (skeleton). Re-fetch when the selected strategy
  // or org changes, and when `result` updates (a new fill refreshes the authoritative result → refresh positions
  // too; qty/avgEntry only move on fills, so this cadence is correct — mark/uPnL may be stale between fills).
  let positions = null;
  let positionsToken = 0;
  let positionsKey = null;
  $: strategyId = strategy?.strategyId ?? null;
  async function loadPositions(id, orgId, resetSkeleton) {
    if (resetSkeleton) positions = null; // show the skeleton while a DIFFERENT strategy/org loads
    if (!id) { positions = []; return; }
    const token = ++positionsToken;
    try {
      const res = await api.getStrategyPositions(id, orgId);
      if (token === positionsToken) positions = Array.isArray(res) ? res : [];
    } catch {
      if (token === positionsToken) positions = [];
    }
  }
  // Re-derive on strategy/org change (reset skeleton) AND on each new authoritative result/fills (silent refresh,
  // no skeleton flash). The key distinguishes a context switch from an in-place result update.
  $: {
    const key = `${strategyId}|${$selectedOrgId}`;
    const contextChanged = key !== positionsKey;
    positionsKey = key;
    result; // track new fills → refresh positions in place
    void loadPositions(strategyId, $selectedOrgId, contextChanged);
  }

  // ── Most-recent-BAR error banner (#3) ─────────────────────────────────────────────────────────────────────
  // The top section shows only the newest error that occurred in the MOST RECENT tick/bar — NOT a persistent
  // callout of a past error. Once a new bar runs with no error, the banner clears (the full durable history lives
  // on the Errors tab; alerts live on the Alerts tab — neither is surfaced at the top). The "most recent bar"
  // window start (barTime) is server-derived (recentBarCounts); we filter the shared, deduped, >=Error source to
  // rows at/after it. Re-read when the strategy/org changes OR a new bar advances (lastBarTime/barCount).
  // Message rendered via {text} interpolation only, NEVER {@html} (XSS defense — the reason is exchange-controlled).
  let errorsStore = strategyErrors(null, null);
  let countsStore = recentBarCounts(null, null);
  let detailsOpen = false; // the inline <details> disclosure; resets on context change (does not persist).
  let bannerKey = null;
  $: {
    const key = `${strategyId}|${$selectedOrgId}|${strategy?.lastBarTime ?? ''}|${strategy?.barCount ?? 0}`;
    if (key !== bannerKey) {
      bannerKey = key;
      detailsOpen = false;
      if (strategyId) {
        errorsStore = strategyErrors(strategyId, $selectedOrgId);
        countsStore = recentBarCounts(strategyId, $selectedOrgId, key);
      }
    }
  }

  // The most-recent-bar window start; an error counts as "this tick" when its time is at/after it. Before the
  // first bar (barTime null) nothing is surfaced (no window yet).
  $: barWindowStart = $countsStore.barTime ? new Date($countsStore.barTime).getTime() : null;
  // The newest error WITHIN the current bar window (the shared list is newest-first). Clears when the bar
  // advances with no new error. A Warning/Info never appears (the source gates on >= Error).
  $: bannerError = barWindowStart == null
    ? null
    : ($errorsStore.find((e) => {
        const t = new Date(e.time).getTime();
        return !Number.isNaN(t) && t >= barWindowStart;
      }) ?? null);
</script>

<div class="at-overview-tab">
  {#if bannerError}
    <!-- Loud most-recent-error callout (U7). Persists until a newer error supersedes it; Error/Critical render
         the same red in v1. Message via {text} interpolation only (no @html — partly exchange-controlled). -->
    <div class="at-error-banner" role="alert">
      <div class="at-error-banner-head">
        <span class="at-error-banner-icon" aria-hidden="true">⚠</span>
        <span class="at-error-banner-text">{bannerError.code}: {bannerError.message}</span>
      </div>
      <details class="at-error-banner-details" bind:open={detailsOpen}>
        <summary>Details</summary>
        <dl class="at-error-banner-dl">
          <div><dt>Time</dt><dd>{fmtTime(bannerError.time)}</dd></div>
          <div><dt>Severity</dt><dd>{bannerError.severity}</dd></div>
          <div><dt>Code</dt><dd>{bannerError.code}</dd></div>
          <div><dt>Message</dt><dd>{bannerError.message}</dd></div>
        </dl>
      </details>
    </div>
  {/if}

  <div class="at-overview-controls">
    <LifecycleControls {status} busy={actionBusy} owner={strategy?.userId ?? null}
      backfillState={strategy?.backfillState ?? 'ready'}
      backfillMessage={strategy?.backfillMessage ?? null}
      collection={strategy?.collectionSymbol ? {
        symbol: strategy.collectionSymbol,
        symbolsDone: strategy.collectionSymbolsDone,
        symbolsTotal: strategy.collectionSymbolsTotal,
        batchesDone: strategy.collectionBatchesDone,
        batchesTotal: strategy.collectionBatchesTotal,
      } : null}
      on:start={onStart} on:pause={onPause} on:resume={onResume}
      on:stop={onStop} on:remove={onRemove} />
    {#if onGoLive && canGoLive}
      <!-- D4 promotion: opens a PREFILLED (Coinbase, Live) deploy (fresh instance) behind the typed-name
           real-money confirm — never a mode flip on this running simulated strategy (ADR-0040). -->
      <button type="button" class="btn at-go-live-btn" on:click={onGoLive} disabled={actionBusy}>
        Promote to Live on Coinbase…
      </button>
    {/if}
  </div>

  {#if loading && !result}
    <div class="at-skeleton-rows"><div class="at-skeleton-row"></div></div>
  {:else if result}
    <PerformancePanel
      {result}
      showTradesTable={false}
      showUnfilledBanner={false}
      noDataTitle="No NAV history yet."
      noDataBody="This strategy hasn't produced any NAV bars yet. Once it ticks, live metrics and charts will appear here." />
    <!-- #5/U6: Current Positions in place of the trades blotter (trades live on the Trades tab). The header
         shows the strategy's available USD (cash) balance — before the first fill the whole allocation is cash,
         so fall back to startingCash until a live cash mark exists. -->
    <PositionsTable {positions} usdBalance={strategy?.cash ?? strategy?.startingCash} cashCurrency={strategy?.quoteCurrency ?? 'USD'} />
    {#if meaningful}
      <div class="backtest-charts">
        <EquityCurveChart data={result.equityCurve} trades={result.trades} />
        <DrawdownChart data={result.equityCurve} />
        <ReturnsChart data={result.equityCurve} />
      </div>
      {#if tearsheet}
        {#key strategy?.strategyId}
          <div class="backtest-charts backtest-charts-tearsheet">
            <RollingVolatilityChart data={tearsheet.rollingVolatility} />
            <RollingSharpeChart data={tearsheet.rollingSharpe} />
            <UnderwaterChart data={tearsheet.underwater} />
            <CumulativeVsBenchmarkChart data={tearsheet.cumulativeVsBenchmark} />
            <AnnualReturnsChart data={tearsheet.annualReturns} />
            <MonthlyReturnsHeatmap data={tearsheet.monthlyHeatmap} />
            <ReturnDistributionChart data={tearsheet.returnDistribution} />
            <ReturnQuantilesChart data={tearsheet.returnQuantiles} />
            <DrawdownPeriodsTable data={tearsheet.drawdownPeriods} />
          </div>
        {/key}
      {/if}
    {/if}
  {:else}
    <div class="empty-state">
      <p class="empty-state-description">No performance data yet for this strategy.</p>
    </div>
  {/if}
</div>

<style>
  @import '../../Backtesting/Backtesting.css';
  .at-overview-controls { margin-bottom: 0.75rem; display: flex; align-items: center; gap: 0.75rem; flex-wrap: wrap; }

  /* Loud most-recent-error banner (U7). A single red callout above the lifecycle controls. */
  .at-error-banner {
    margin-bottom: 0.85rem; padding: 0.7rem 0.9rem; border-radius: 6px;
    background: rgba(224, 83, 58, 0.12); border: 1px solid var(--danger, #e0533a);
    border-left: 4px solid var(--danger, #e0533a);
  }
  .at-error-banner-head { display: flex; align-items: center; gap: 0.5rem; }
  .at-error-banner-icon { color: var(--danger, #e0533a); font-size: 1rem; line-height: 1; }
  .at-error-banner-text { font-weight: 700; color: var(--danger, #e0533a); word-break: break-word; }
  .at-error-banner-details { margin-top: 0.4rem; }
  .at-error-banner-details summary {
    cursor: pointer; font-size: 0.78rem; font-weight: 600; color: var(--text-secondary, #b5b6b8);
  }
  .at-error-banner-details summary:focus-visible { outline: 2px solid var(--accent, #3c78f0); outline-offset: 2px; }
  .at-error-banner-dl { margin: 0.5rem 0 0; display: grid; gap: 0.25rem; font-size: 0.8rem; }
  .at-error-banner-dl > div { display: grid; grid-template-columns: 6rem 1fr; gap: 0.5rem; }
  .at-error-banner-dl dt { color: var(--text-muted, #8a8b8d); font-weight: 600; }
  .at-error-banner-dl dd { margin: 0; color: var(--text-primary, #e8e9ea); word-break: break-word; }
</style>
